import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario6SurgeryWard, { scenario6SurgeryWardAudioCaching, scenario6SurgeryWardVideoSegments } from "../../../Scenario6-surgery-ward";
import { SurgeryWardComponentsStep1ComponentMainConfig, SurgeryWardComponentsStep1ComponentPart1Config } from "../surgery-ward-components-step1";


export default class SurgeryWardComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): SurgeryWardComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: SurgeryWardComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
				<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
				thing in similar circumstances.<br/><br/>
				Nurse #1: I admit that it gets pretty busy on our unit, but this type of event 
				rarely happens. We have so many safeguards in place that it would take a lot of missed steps for a patient 
				to get someone else’s medication. I can’t even recall the last time that this has happened; 
				it’s been years!<br/><br/>
				Nurse #2: All I know is there are many, many times where the amount of work 
				exceeds my ability to stay ahead of the work. I have not made this type of mistake, but I can tell you, 
				even with the procedures we have to follow, I can see how this could happen.<br/><br/>
				Nurse #3: It can get super busy on the unit. The issue is interruptions - call 
				lights, phone calls - it’s hard to stay focused on a task. Although our team is pretty good at communicating 
				and following safety guidance, I can see how this could happen. I’m glad it wasn’t me.</p>
			`,
			okButton: 'Continue',
			talkingVideoSrc: scenario6SurgeryWardVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario6SurgeryWardVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			audioUrl: scenario6SurgeryWardAudioCaching.getItemByKey(scenario6SurgeryWardAudioCaching.audioKeys.question3).src,
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 1'),
			isYesCorrect: true,
			correctFeedback: `
				<p class="default-text">Correct. There is no question that all nurses identified the presence of interruptions, 
				high workload, and the ability to stay focused and complete tasks. While one nurse felt that the event rarely happens, 
				the general sentiment is that this error is understandable, predictable, and the conditions that made it so were acknowledged by all.</p>
			`,
			wrongFeedback: `
				<p class="default-text">Incorrect. This is not a situation that can be contributed solely to this one nurse’s decisions and actions.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario6SurgeryWardVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			audioUrl: scenario6SurgeryWardAudioCaching.getItemByKey(scenario6SurgeryWardAudioCaching.audioKeys.question4).src,
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 2'),
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option There are multiple systems issues at play here.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. There are multiple systems issues at 
						play here. High workload to the point of task saturation, the inability for staff to keep 
						up with call lights, the lack of timely follow up by other team members (resident physician 
							not answering pages), a unit that is replete with distractions and interruptions which 
							reduces a clinician’s situational awareness and ability to comply with safety protocols. 
							With human error, the accountability for this event lies with the system and the 
							leaders that create and maintain that system.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. The substitution test demonstrates that there are multiple systems issues at play here.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				const mentorInfo = scenario6SurgeryWardVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Lies completely with the employee.');
			}
		}, 3);
	}
}